import type { ReactElement } from 'react'
import { useState, cloneElement } from 'react'

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingFocusManager,
  FloatingPortal
} from '@floating-ui/react'

import ActionsMenuItem from './item'

let actionsMenuRoot = document.getElementById('ActionsMenu')
if (!actionsMenuRoot) {
  actionsMenuRoot = document.createElement('div')
  actionsMenuRoot.setAttribute('id', 'ActionsMenu')
  document.body.appendChild(actionsMenuRoot)
}

type ActionsMenuProps = {
  trigger: ReactElement
  children: Array<ReactElement<typeof ActionsMenuItem>>
}

function ActionsMenu({ children, trigger }: ActionsMenuProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const offsetValue = 10
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement: 'bottom-end',
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(offsetValue), flip(), shift()]
  })

  const role = useRole(context)
  const click = useClick(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  return (
    <>
      {cloneElement(trigger, { ref: refs.setReference, ...getReferenceProps() })}
      {isOpen && (
        <FloatingPortal root={actionsMenuRoot}>
          <FloatingFocusManager context={context} modal={false} disabled>
            <ul
              className='_bg-primary _rounded _shadow-md _py2 _list-none'
              ref={refs.setFloating}
              style={{ zIndex: 100, minWidth: 100, ...floatingStyles }}
              {...getFloatingProps()}>
              {children}
            </ul>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}

ActionsMenu.Item = ActionsMenuItem

export default ActionsMenu
